import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiErr } from "@/lib/api";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const BG = "https://images.pexels.com/photos/36499784/pexels-photo-36499784.jpeg";
const BLUE_CROSS_LOGO = "/cross-logo.png";
const THERAPEUTIC_YOGA_LOGO = "/therapeutic-yoga-logo.png";
const CREDIT = "Developed by in Collaboration Ortus Enterprises / Rubik Creation – 9326637522";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email.trim(), password);
      toast.success(`Welcome back, ${u.name}`);
      navigate(u.role === "admin" ? "/admin" : "/mr");
    } catch (err) {
      toast.error(apiErr(err, "Login failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Visual */}
      <div className="relative hidden lg:block">
        <img src={BG} alt="Yoga at sunrise" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-emerald-950/90 via-emerald-900/40 to-transparent" />
        <div className="relative z-10 flex h-full flex-col justify-between p-12 text-white">
          <div className="flex items-center justify-between gap-4">
            <div className="inline-flex items-center rounded-xl bg-white/95 px-4 py-3 backdrop-blur">
              <img src={THERAPEUTIC_YOGA_LOGO} alt="Therapeutic Yoga" className="h-14 w-auto" />
            </div>
            <div className="inline-flex items-center rounded-xl bg-white/95 px-4 py-2.5 backdrop-blur">
              <img src={BLUE_CROSS_LOGO} alt="Blue Cross Life Sciences" className="h-9 w-auto" />
            </div>
          </div>
          <div>
            <h1 className="font-heading text-4xl xl:text-5xl font-bold leading-tight">
              Yoga Day<br />Greeting Studio
            </h1>
            <p className="mt-4 max-w-md text-emerald-100/90 text-lg">
              Turn doctors' wishes into beautifully branded greeting videos in one click.
            </p>
            <p className="mt-8 text-xs text-emerald-100/70">
              {CREDIT}
            </p>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="relative flex flex-col items-center justify-center px-6 py-12 bg-stone-50">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="lg:hidden mb-8">
            <img src={BLUE_CROSS_LOGO} alt="Blue Cross Life Sciences" className="h-9 w-auto" />
          </div>
          <h2 className="font-heading text-3xl font-semibold text-stone-900">Sign in</h2>
          <p className="mt-2 text-stone-500">Access your dashboard to manage greeting videos.</p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-stone-500">Employee ID or Email</label>
              <input
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-2 flex h-11 w-full rounded-md border border-stone-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                placeholder="EMP1024 or you@bluecross.com"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-stone-500">Password</label>
              <div className="relative mt-2">
                <input
                  data-testid="login-password-input"
                  type={show ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="flex h-11 w-full rounded-md border border-stone-300 bg-white px-3 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="••••••••"
                />
                <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600">
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="flex justify-end">
              <Link to="/forgot-password" data-testid="forgot-password-link" className="text-sm font-medium text-emerald-700 hover:text-emerald-800">
                Forgot password?
              </Link>
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="inline-flex h-11 w-full items-center justify-center rounded-md bg-emerald-600 text-sm font-semibold text-white transition-colors hover:bg-emerald-700 disabled:opacity-60"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
            </button>
          </form>

          {/* Credit — mobile only (image panel hidden on small screens) */}
          <p className="lg:hidden mt-10 text-center text-xs text-stone-400">
            {CREDIT}
          </p>
        </div>
      </div>
    </div>
  );
}
